import { Button, Card, Grid2, List, ListItem, ListItemIcon, ListItemText, TextField, Typography, Chip, useTheme } from "@mui/material";
import { useShopSlice } from "@store/slices/shop";
import CircleIcon from "@mui/icons-material/Circle";
import { useState, useEffect } from "react";
import { alpha } from "@mui/material/styles";
import { color } from "@/app/style/color";

export const yearlyDiscount = 0.9; // 10%

interface SubscriptionCard {
    title: string;
    monthlyPrice: number;
    features: string[];
    isHighlight?: boolean;
    onClick?: () => void;

    // Product type for explicit handling
    productType?: 'SUBSCRIPTION_SEAT' | 'ONE_TIME_PURCHASE' | 'SERVICE_CREDIT';

    // New properties
    priceInterval?: string; // 'monthly' or other intervals
    isOnRequest?: boolean; // If true, price is "on request"

    onQuantityChange?: (quantity: number) => void; // Callback for quantity changes
    initialQuantity?: number; // Initial quantity value

    // Seat management properties
    currentSeats?: number; // Current number of seats for existing subscriptions
    showCurrentSeats?: boolean; // Whether to show current seat information

    // TODO Wird mandatory
    priceId?: string;

    // Zeigt weder Button noch Eingabefeld unten an und verringert die Card-Höhe
    hideButton?: boolean;

    // AB x Euro
    isStartingAt?: boolean;

    // Controls whether the card uses the gradient background.
    useGradient?: boolean;
}

const SubscriptionCard = ({
    title,
    monthlyPrice,
    features,
    isHighlight,
    priceInterval = "monthly",
    priceId,
    isOnRequest = false,
    onQuantityChange,
    initialQuantity = 0,
    currentSeats,
    showCurrentSeats = false,
    hideButton,
    isStartingAt,
    onClick,
    productType,
    useGradient = false,
}: SubscriptionCard) => {
    const theme = useTheme();
    const { period, checkoutStatus } = useShopSlice();
    
    // Always show the quantity from Redux store (initialQuantity)
    // For seat subscriptions with existing seats, we'll convert this to "additional seats" display
    const [quantity, setQuantity] = useState<number | string>(initialQuantity);

    // Update quantity when initialQuantity changes – but DON'T override an empty input that the user is currently editing.
    useEffect(() => {
        // If the user cleared the field and it is still focused (quantity === ''),
        // skip syncing so the UI doesn't jump back to 0 while they type.
        if (quantity === '') {
            return;
        }

        let displayQuantity: number;
        
        if (productType === 'SUBSCRIPTION_SEAT' && showCurrentSeats && currentSeats !== undefined) {
            // For existing seat subscriptions: show additional seats (total from Redux - current seats)
            displayQuantity = Math.max(0, initialQuantity - currentSeats);
        } else {
            // For all other cases: show total quantity from Redux store
            displayQuantity = initialQuantity;
        }
        
        setQuantity(displayQuantity);
    }, [initialQuantity, currentSeats, showCurrentSeats, productType, quantity]);

    const isMonthlyInterval = priceInterval === "monthly";
    const isYearlyInterval = priceInterval === "year";

    const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        
        // Allow empty string (for deletion/clearing)
        if (inputValue === '') {
            setQuantity('');
            if (onQuantityChange) {
                // For existing seat subscriptions: current seats + 0 additional = current seats
                // For other products: 0
                const actualQuantity = (productType === 'SUBSCRIPTION_SEAT' && showCurrentSeats) 
                    ? (currentSeats || 0) 
                    : 0;
                onQuantityChange(actualQuantity);
            }
            return;
        }
        
        const parsedValue = parseInt(inputValue);
        // 🔒 SECURITY: Enhanced input validation
        const newQuantity = isNaN(parsedValue) ? 0 : Math.max(0, parsedValue);

        // 🔒 SECURITY: Additional validation for suspicious values
        if (parsedValue > 1000) {
            console.warn("🚨 SECURITY: User attempted to enter suspiciously high quantity", {
                attempted: parsedValue,
                capped: newQuantity,
                priceId,
            });
        }

        setQuantity(newQuantity);
        if (onQuantityChange) {
            // For existing seat subscriptions: convert additional seats to total seats
            // For all other products: use quantity directly
            const actualQuantity = (productType === 'SUBSCRIPTION_SEAT' && showCurrentSeats) 
                ? (currentSeats || 0) + newQuantity 
                : newQuantity;
            onQuantityChange(actualQuantity);
        }
    };

    const getPrice = () => {
        // Don't handle isOnRequest here, it's handled in the JSX

        let price;
        if (isYearlyInterval) {
            // Yearly subscription - use price directly without any discount
            return new Intl.NumberFormat("de-DE", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(monthlyPrice);
        } else if (!isMonthlyInterval) {
            // One-time payment
            return new Intl.NumberFormat("de-DE", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(monthlyPrice);
        }

        // INTERVAL 📅 Force yearly pricing since monthly is disabled - restore commented logic to re-enable monthly
        // if (period === "monthly") {
        //     price = monthlyPrice;
        // } else {
        //     price = monthlyPrice * yearlyDiscount;
        // }
        
        // Force yearly pricing
        price = monthlyPrice * yearlyDiscount;

        // Formatieren des Preises im deutschen Format
        return new Intl.NumberFormat("de-DE", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(price);
    };

    const getPriceYearly = () => {
        if (isOnRequest || !isMonthlyInterval) {
            return null; // Don't show yearly price for on-request or one-time payments
        }

        // INTERVAL 📅 Always show yearly total since we only offer yearly - restore commented logic to re-enable monthly
        // let price;
        // if (period === "monthly") {
        //     price = monthlyPrice * 12;
        // } else {
        //     price = monthlyPrice * 12 * yearlyDiscount;
        // }
        
        // Force yearly total calculation
        const price = monthlyPrice * 12 * yearlyDiscount;

        // Formatieren des Preises im deutschen Format
        return new Intl.NumberFormat("de-DE", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(price);
    };

    const getQuantityLabel = () => {
        if (productType === 'SUBSCRIPTION_SEAT' && showCurrentSeats) {
            return `Zusätzliche Sitze (aktuell: ${currentSeats || 0})`;
        }
        return "Anzahl";
    };

    const getHelperText = () => {
        if (productType !== 'SUBSCRIPTION_SEAT' || !showCurrentSeats) {
            return undefined;
        }

        const additionalSeats = typeof quantity === 'number' ? quantity : 0;
        const currentSeatsCount = currentSeats || 0;
        const newTotalSeats = currentSeatsCount + additionalSeats;

        if (additionalSeats > 0) {
            return `Neue Sitzzahl: ${newTotalSeats}`;
        } else {
            return `Aktuelle Sitzzahl: ${currentSeatsCount}`;
        }
    };

    const getCardBackgroundColor = () => {
        if (useGradient) {
            return `linear-gradient(to bottom right, ${alpha(theme.palette.semantic.button.primary, 0.2)}, ${alpha(theme.palette.semantic.button.primary, 0.5)} 20%, white)`;
        }
        return undefined;
    };

    const getCardBorderColor = (plan: "S" | "M" | "L") => {
        return `2px solid ${theme.palette.semantic.planSize[plan]}`;
    };

    const getCardBorder = () => {
        switch (title) {
            case "S":
                return getCardBorderColor("S");
            case "M":
                return getCardBorderColor("M");
            case "L":
                return getCardBorderColor("L");
            default:
                return undefined;
        }
    };

    // Extracted card markup into a stable React element to avoid remounting (which caused input focus loss)
    const mainCardElement = (
        <Card
            sx={{
                height: hideButton ? 280 : 360,
                width: 260,
                border: getCardBorder(),
                background: getCardBackgroundColor(),
            }}
        >
            <Grid2 container size={12} spacing={0.5} px={3} py={2.5} direction={"column"} height={"100%"}>
                <Grid2 container alignItems="center" spacing={1}>
                    <Grid2>
                        <Typography variant={"h5"} fontSize={18}>
                            {title}
                        </Typography>
                    </Grid2>

                    {showCurrentSeats && (
                        <Grid2 sx={{ marginLeft: "auto" }}>
                            <Chip
                                label={`${currentSeats || 0} aktuelle Sitze`}
                                size="small"
                                variant="outlined"
                                sx={{ color: color.brand.text, border: `1px solid ${color.brand.text}` }}
                            />
                        </Grid2>
                    )}
                </Grid2>

                <Grid2 container alignItems={"baseline"} spacing={0.5}>
                    {isStartingAt && (
                        <Grid2>
                            <Typography fontSize={14}>ab</Typography>
                        </Grid2>
                    )}

                    <Grid2>
                        <Typography variant={"h3"} fontWeight={"bold"}>
                            {isOnRequest ? "Auf Anfrage" : `${getPrice()} €`}
                        </Typography>
                    </Grid2>

                    {isMonthlyInterval && !isOnRequest && !isStartingAt && (
                        <Grid2>
                            <Typography fontSize={12}>/Monat</Typography>
                        </Grid2>
                    )}

                    {isYearlyInterval && !isOnRequest && !isStartingAt && (
                        <Grid2>
                            <Typography fontSize={12}>/Jahr</Typography>
                        </Grid2>
                    )}

                    {!isMonthlyInterval && !isYearlyInterval && !isOnRequest && !isStartingAt && (
                        <Grid2>
                            <Typography fontSize={12}>(einmalig)</Typography>
                        </Grid2>
                    )}
                </Grid2>

                {isMonthlyInterval && !isOnRequest && !isStartingAt && (
                    <Grid2 container alignItems={"baseline"} spacing={0.5}>
                        <Grid2>
                            <Typography fontWeight={500} fontSize={13}>
                                {getPriceYearly()} €
                            </Typography>
                        </Grid2>

                        <Grid2>
                            <Typography fontSize={11}>/Jahr</Typography>
                        </Grid2>
                    </Grid2>
                )}

                <Grid2 flexGrow={1}>
                    <List>
                        {features.map((feature, index) => (
                            <ListItem key={index} sx={{ p: 0 }}>
                                <ListItemIcon sx={{ minWidth: 16 }}>
                                    <CircleIcon sx={{ fontSize: 6, color: "semantic.text.paragraph" }} />
                                </ListItemIcon>
                                <ListItemText primary={feature} slotProps={{ primary: { fontSize: 12 } }} />
                            </ListItem>
                        ))}
                    </List>
                </Grid2>

                {!hideButton && (
                    <Grid2>
                        {isOnRequest ? (
                            <Button variant="contained" fullWidth size="small" sx={{ mt: 1 }} onClick={onClick}>
                                Anfragen
                            </Button>
                        ) : (
                            <TextField
                                label={getQuantityLabel()}
                                type="number"
                                size="small"
                                value={quantity}
                                onChange={handleQuantityChange}
                                inputProps={{ min: 0 }}
                                fullWidth
                                sx={{ mt: 1 }}
                                disabled={checkoutStatus === "loading" || checkoutStatus === "error"}
                                helperText={getHelperText()}
                            />
                        )}
                    </Grid2>
                )}
            </Grid2>
        </Card>
    );

    return isHighlight ? (
        <Card variant={"outlined"} sx={{ p: 1, backgroundColor: alpha(theme.palette.semantic.planSize["M"], 0.25) }}>
            {mainCardElement}

            <Typography fontSize={15} fontWeight={500} align={"center"} sx={{ pt: 1.5, pb: 0.5 }}>
                Unsere Empfehlung
            </Typography>
        </Card>
    ) : (
        mainCardElement
    );
};

export default SubscriptionCard;
